import { Globe, Sparkles, Send } from "lucide-react"
import { Link, router, usePage } from "@inertiajs/react"
import type { SharedData } from "@/types"

import AudioPlayer from "@/components/conversation/audio-player"
import GuruExplanationLoader from "@/components/conversation/guru-explanation-loader"
import TranslationLoader from "@/components/conversation/translation-loader"
import AudioLoader from "@/components/conversation/audio-loader"
import RomanizationLoader from "@/components/conversation/romanization-loader"
import { ThaiFlagMap } from "@/components/icons/thai-flag-map"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"

// Dummy translation dictionary
const TRANSLATIONS: Record<
  string,
  { translation: string; romanization?: string }
> = {
  // English → Thai
  Hello: { translation: "สวัสดี", romanization: "sawatdee" },
  "How are you?": { translation: "สบายดีไหม", romanization: "sabai dee mai" },
  "Thank you": { translation: "ขอบคุณ", romanization: "khob khun" },

  // Thai → English
  สวัสดี: { translation: "Hello", romanization: "sawatdee" },
  สบายดีไหม: { translation: "How are you?", romanization: "sabai dee mai" },
  ขอบคุณ: { translation: "Thank you", romanization: "khob khun" },
}

// Get translation for text
const getTranslation = (
  text: string,
): { translation?: string; romanization?: string } => {
  const trimmedText = text.trim()

  // Check if we have a direct translation
  if (TRANSLATIONS[trimmedText]) {
    return TRANSLATIONS[trimmedText]
  }

  // Fallback translation
  // Return undefined so the loader can be shown
  return {}
}

interface ExplanationData {
  context?: string
  breakdown?: string
  grammar_points?: string[]
}

interface MessageProps {
  id: string
  text: string
  language: "EN" | "TH"
  timestamp: Date
  audio_url?: string
  translation?: string
  romanization?: string
  explanation?: string | ExplanationData
  explanationError?: string
  romanizationError?: string
}

export default function Message({
  id,
  text,
  language,
  timestamp,
  audio_url,
  translation: propTranslation,
  romanization: propRomanization,
  explanation,
  explanationError,
  romanizationError,
}: MessageProps) {
  const { auth } = usePage<SharedData>().props
  const canPublish = auth?.user?.can_publish ?? false

  // Use props if available, otherwise fallback to local logic (or just use props)
  // For now, let's prioritize props and fallback to getTranslation
  const localTranslation = getTranslation(text)
  const translation = propTranslation || localTranslation.translation
  const romanization = propRomanization // Remove local fallback to allow loading state
  const isThai = language === "TH"
  const isEnglish = language === "EN"

  // Format timestamp
  const formattedTime = timestamp.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })

  return (
    <div
      className={cn(
        "group mb-6 flex w-full gap-3",
        isEnglish ? "flex-row-reverse" : "flex-row",
      )}
      data-message-id={id}
    >
      {/* Avatar */}
      <Avatar
        className={cn(
          "size-10 shrink-0",
          isEnglish
            ? "bg-blue-100 dark:bg-blue-900"
            : "bg-slate-100 dark:bg-slate-800",
        )}
      >
        <AvatarFallback
          className={cn(
            isEnglish
              ? "bg-blue-100 dark:bg-blue-900"
              : "bg-slate-100 dark:bg-slate-800",
          )}
        >
          {isEnglish ? (
            <Globe className="size-5 text-blue-600 dark:text-blue-400" />
          ) : (
            <ThaiFlagMap className="size-6" />
          )}
        </AvatarFallback>
      </Avatar>

      {/* Message content */}
      <div
        className={cn(
          "flex max-w-[85%] flex-col gap-2",
          isEnglish ? "items-end" : "items-start",
        )}
      >
        {/* Message bubble */}
        {/* TODO: Experiment with min-width value for different UX
            - Current: min-w-[200px] = More consistent sizing
            - Industry standard (WhatsApp/iMessage): No minimum or min-w-[48px] = More adaptive
            - Try removing min-w entirely to let content dictate bubble width
        */}
        <div
          className={cn(
            "inline-flex max-w-full min-w-[200px] flex-col gap-3 rounded-2xl p-4 shadow-sm",
            isEnglish
              ? "bg-blue-500 text-white dark:bg-blue-600"
              : "border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800",
          )}
        >
          {/* Original message */}
          <div className="flex flex-col gap-1">
            <p
              className={cn(
                "text-base leading-relaxed",
                isEnglish ? "text-white" : "text-slate-900 dark:text-white",
              )}
            >
              {text}
            </p>
          </div>

          {/* Divider */}
          <div
            className={cn(
              "h-px",
              isEnglish
                ? "bg-blue-400 dark:bg-blue-500"
                : "bg-slate-200 dark:bg-slate-700",
            )}
          />

          {/* Translation section */}
          {translation ? (
            <div className="flex flex-col gap-1.5">
              <span
                className={cn(
                  "text-xs font-medium tracking-wide uppercase",
                  isEnglish
                    ? "text-blue-100 dark:text-blue-200"
                    : "text-slate-500 dark:text-slate-400",
                )}
              >
                {isThai ? "English" : "Translation"}
              </span>
              <p
                className={cn(
                  "text-sm leading-relaxed",
                  isEnglish
                    ? "text-blue-50 dark:text-blue-100"
                    : "text-slate-700 dark:text-slate-300",
                )}
              >
                {translation}
              </p>

              {/* Audio player for translation */}
              {audio_url ? (
                <AudioPlayer
                  audioUrl={audio_url}
                  variant={isEnglish ? "light" : "dark"}
                  className="mt-2"
                />
              ) : (
                <AudioLoader isEnglish={isEnglish} />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <TranslationLoader isEnglish={isEnglish} />
              <AudioLoader isEnglish={isEnglish} />
            </div>
          )}

          {romanization ? (
            <div className="flex flex-col gap-1">
              <span
                className={cn(
                  "text-xs font-medium tracking-wide uppercase",
                  isEnglish
                    ? "text-blue-100 dark:text-blue-200"
                    : "text-slate-500 dark:text-slate-400",
                )}
              >
                Romanization
              </span>
              <p
                className={cn(
                  "text-sm leading-relaxed italic",
                  isEnglish
                    ? "text-blue-50 dark:text-blue-100"
                    : "text-slate-600 dark:text-slate-400",
                )}
              >
                {romanization}
              </p>
            </div>
          ) : (
            <RomanizationLoader
              isEnglish={isEnglish}
              error={romanizationError}
            />
          )}

          {/* Explanation Loader / Collapsible */}
          <GuruExplanationLoader
            id={id}
            isLoading={!explanation && !explanationError}
            explanation={explanation}
            isEnglish={isEnglish}
            error={explanationError}
          />

          {/* Flashcard creation button with progressive loading states */}
          {translation &&
            audio_url &&
            (() => {
              const isFlashcardReady = !!(romanization && explanation)

              // If not ready, show disabled loading state
              if (!isFlashcardReady) {
                return (
                  <button
                    disabled
                    className={cn(
                      "inline-flex cursor-not-allowed items-center gap-1.5 text-xs font-medium opacity-50 transition-opacity",
                      isEnglish
                        ? "text-blue-100 dark:text-blue-200"
                        : "text-slate-500 dark:text-slate-400",
                    )}
                    title="Waiting for Guru analysis to complete"
                  >
                    <Sparkles className="size-3.5 animate-pulse" />
                    <span className="animate-pulse">
                      Guru analyzing...
                    </span>
                  </button>
                )
              }

              // Ready state - interactive links with hover effects
              return (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <Link
                    href={`/flashcards/new?message_id=${id}`}
                    className={cn(
                      "inline-flex items-center gap-1.5 text-xs font-medium transition-all duration-200 hover:scale-105",
                      isEnglish
                        ? "text-blue-100 hover:text-white dark:text-blue-200 dark:hover:text-white"
                        : "text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300",
                    )}
                  >
                    <Sparkles className="size-3.5" />
                    Create flashcard
                  </Link>

                  {canPublish && (
                    <button
                      type="button"
                      onClick={() => router.post("/editorial/phrases", { message_id: id })}
                      className={cn(
                        "inline-flex items-center gap-1.5 text-xs font-medium transition-all duration-200 hover:scale-105",
                        isEnglish
                          ? "text-blue-100 hover:text-white dark:text-blue-200 dark:hover:text-white"
                          : "text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300",
                      )}
                    >
                      <Send className="size-3.5" />
                      Publish phrase
                    </button>
                  )}
                </div>
              )
            })()}
        </div>

        {/* Timestamp */}
        <span className="px-1 text-xs text-slate-400 dark:text-slate-500">
          {formattedTime}
        </span>
      </div>
    </div>
  )
}
